import { useEffect, useState, useCallback } from 'react';
import { api } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { CurrencyEur, Plus, PencilSimple, Trash, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatCurrency } from './formatters';

const displayAmount = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const s = String(v).trim();
  const n = parseFloat(s.replace(',', '.'));
  // Se è un numero puro (senza altri caratteri), formattalo come valuta
  if (!isNaN(n) && /^-?\d+([.,]\d+)?$/.test(s)) return formatCurrency(n);
  // Altrimenti mostra il testo libero (es. "2 x 50")
  return s;
};

const EMPTY_FORM = {
  anno: '',
  codice: '',
  descrizione: '',
  quota_associativa: '',
  scadenza_quota: '',
  prezzo_rata: '',
  scadenza_1: '',
  scadenza_2: '',
  scadenza_3: '',
};

export function ListiniSection({ anno }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getListini(anno); setItems(res.data || []); }
    catch { toast.error('Errore caricamento listini'); }
    finally { setLoading(false); }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, anno: anno || '' });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      anno: item.anno || '',
      codice: item.codice || '',
      descrizione: item.descrizione || '',
      quota_associativa: item.quota_associativa || '',
      scadenza_quota: item.scadenza_quota || '',
      prezzo_rata: item.prezzo_rata || '',
      scadenza_1: item.scadenza_1 || '',
      scadenza_2: item.scadenza_2 || '',
      scadenza_3: item.scadenza_3 || '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.codice) { toast.error('Codice obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...form, anno: form.anno ? parseInt(form.anno) : null };
      if (editing) { await api.updateListino(editing.id, payload); toast.success('Listino aggiornato'); }
      else { await api.createListino(payload); toast.success('Listino creato'); }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminare il listino "${item.codice} - ${item.descrizione}"?`)) return;
    try { await api.deleteListino(item.id); toast.success('Listino eliminato'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Listini</h1>
          <p className="text-[#52525B] text-sm">Tariffe e scadenze pagamenti per l&apos;anno {anno || '-'}</p>
        </div>
        <Button onClick={openNew} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="add-listino-btn">
          <Plus size={16} className="mr-1" />Nuovo Listino
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm">
          <Tag size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">Nessun listino per l&apos;anno {anno}</p>
          <p className="text-xs text-[#A1A1AA] mt-1">Clicca &quot;Nuovo Listino&quot; per crearne uno.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="listini-table">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Codice</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Descrizione</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Quota Assoc.</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Scad. Quota</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Rata</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Scad. 1/2/3</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                  <td className="px-3 py-2 font-bold font-['Work_Sans']">{it.codice}</td>
                  <td className="px-3 py-2">{it.descrizione}</td>
                  <td className="px-3 py-2 text-right font-['Work_Sans']">{displayAmount(it.quota_associativa)}</td>
                  <td className="px-3 py-2 text-center text-xs">{it.scadenza_quota || '-'}</td>
                  <td className="px-3 py-2 text-right font-['Work_Sans']">{displayAmount(it.prezzo_rata)}</td>
                  <td className="px-3 py-2 text-center text-xs">
                    {[it.scadenza_1, it.scadenza_2, it.scadenza_3].filter(Boolean).join(' / ') || '-'}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-[#F4F4F5] text-[#52525B]" title="Modifica" data-testid={`edit-listino-${it.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => handleDelete(it)} className="p-1.5 rounded hover:bg-red-50 text-red-600 ml-1" title="Elimina" data-testid={`delete-listino-${it.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">{editing ? 'Modifica Listino' : 'Nuovo Listino'}</DialogTitle>
            <DialogDescription>Definisci tariffe e scadenze. Il listino verrà selezionabile quando invii la mail di conferma iscrizione.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="label-overline">Anno</Label>
                <Input type="number" value={form.anno} onChange={e => setForm({...form, anno: e.target.value})} placeholder="2026" className="rounded-sm" data-testid="listino-anno-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="label-overline">Codice *</Label>
                <Input value={form.codice} onChange={e => setForm({...form, codice: e.target.value.toUpperCase()})} placeholder="A, B, ADULTI..." className="rounded-sm uppercase" required data-testid="listino-codice-input" />
              </div>
            </div>
            <div>
              <Label className="label-overline">Descrizione</Label>
              <Input value={form.descrizione} onChange={e => setForm({...form, descrizione: e.target.value})} placeholder="Es: Corso individuale 30 min" className="rounded-sm" data-testid="listino-descrizione-input" />
            </div>
            <div className="border-t border-[#E4E4E7] pt-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-2 flex items-center gap-1"><CurrencyEur size={14} />Quota Associativa</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">Importo</Label>
                  <Input value={form.quota_associativa} onChange={e => setForm({...form, quota_associativa: e.target.value})} placeholder="50 oppure 2 x 50" className="rounded-sm" data-testid="listino-quota-input" />
                </div>
                <div>
                  <Label className="label-overline">Scadenza</Label>
                  <Input value={form.scadenza_quota} onChange={e => setForm({...form, scadenza_quota: e.target.value})} placeholder="Es: 15/09/2026" className="rounded-sm" data-testid="listino-scad-quota-input" />
                </div>
              </div>
            </div>
            <div className="border-t border-[#E4E4E7] pt-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-2 flex items-center gap-1"><CurrencyEur size={14} />Rate</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="label-overline">Prezzo Rata</Label>
                  <Input value={form.prezzo_rata} onChange={e => setForm({...form, prezzo_rata: e.target.value})} placeholder="150 oppure 2 x 75" className="rounded-sm" data-testid="listino-rata-input" />
                </div>
                <div>
                  <Label className="label-overline">Scadenza 1</Label>
                  <Input value={form.scadenza_1} onChange={e => setForm({...form, scadenza_1: e.target.value})} placeholder="15/10/2026 o ALL'ISCRIZIONE" className="rounded-sm" data-testid="listino-scad1-input" />
                  <p className="text-[10px] text-[#A1A1AA] mt-1">Puoi inserire una data o la dicitura &quot;ALL&apos;ISCRIZIONE&quot;</p>
                </div>
                <div>
                  <Label className="label-overline">Scadenza 2</Label>
                  <Input value={form.scadenza_2} onChange={e => setForm({...form, scadenza_2: e.target.value})} placeholder="15/01/2027" className="rounded-sm" data-testid="listino-scad2-input" />
                </div>
                <div>
                  <Label className="label-overline">Scadenza 3</Label>
                  <Input value={form.scadenza_3} onChange={e => setForm({...form, scadenza_3: e.target.value})} placeholder="15/04/2027" className="rounded-sm" data-testid="listino-scad3-input" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" disabled={saving} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="save-listino-btn">
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Crea')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
